import {getFullMonthText} from '@wix/events-moment-commons'
import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {widgetDataHooks as DH} from '@wix/wix-events-data-hooks'
import {ChevronDown} from '@wix/wix-ui-icons-common'
import {
  ActionsMenuLayout,
  DatePickerLocale,
  HoverStyles,
  TabItem,
  TextButton,
  TextButtonPriority,
} from '@wix/wix-ui-tpa/cssVars'
import {useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useEffect, useRef, useState} from 'react'
import {useSettings} from '../../../../../../commons/hooks/use-settings'
import {getNullableCategoryId, isCategoryMenuMode} from '../../../../../../commons/selectors/categories'
import {getLocale, getTimezone} from '../../../../../../commons/selectors/environment'
import settingsParams from '../../../../settingsParams'
import {useWidgetActions, useWidgetState} from '../../../hooks/state-provider'
import {useVisibilityStyles} from '../../../hooks/use-visibility-styles'
import {
  getDateInTimezone,
  getReferenceDay,
  getReferenceDayForSelectedDate,
  getReferenceMonthForSelectedDate,
  getToday,
  isCalendarPageLoading,
} from '../../../selectors/calendar-layout'
import {getCategoryTabItems, getSelectedCategoryTabItem} from '../../../selectors/categories'
import {getCompId} from '../../../selectors/component'
import {getCalendarWeekStartDay} from '../../../selectors/list-settings'
import {CalendarLoading} from '../../../types/state'
import {Spinner} from '../../spinner'
import {TodayButton} from '../../today-button'
import s from './header.scss'
import {classes} from './header.st.css'
import DatePicker from './lazy-date-picker'

export const Header = () => {
  const {t} = useTranslation()
  const {get: getSetting} = useSettings()

  const timezone = useWidgetState(getTimezone)
  const referenceDay = useWidgetState(getReferenceDay)
  const todayReference = useWidgetState(getToday)
  const locale = useWidgetState(getLocale) as DatePickerLocale
  const todayReferenceDate = useWidgetState(state => getDateInTimezone(state, todayReference))
  const referenceDayDate = useWidgetState(state => getDateInTimezone(state, referenceDay))
  const startWeekDay = useWidgetState(getCalendarWeekStartDay)
  const categories = useWidgetState(state => getCategoryTabItems(state, getSetting, t))
  const selectedCategory = useWidgetState(state => getSelectedCategoryTabItem(state, getSetting, t))
  const componentId = useWidgetState(getCompId)
  const loading = useWidgetState(isCalendarPageLoading)

  const categoryPickerVisible = isCategoryMenuMode(getSetting)

  const {
    allBreakpoints: {isListMainTitleEnabled},
  } = useVisibilityStyles()
  const title = getSetting(settingsParams.listGeneralTitle)

  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false)

  const datePickerRef = useRef<HTMLDivElement>(null)
  const categoryPickerRef = useRef<HTMLDivElement>(null)

  const {loadCalendar, changeCalendarCategory} = useWidgetActions()

  useEffect(() => {
    const closePickers = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setDatePickerOpen(false)
      }

      if (categoryPickerRef.current && !categoryPickerRef.current.contains(event.target as Node)) {
        setCategoryPickerOpen(false)
      }
    }

    if (datePickerOpen || categoryPickerOpen) {
      document.addEventListener('mousedown', closePickers)
    }

    return () => {
      document.removeEventListener('mousedown', closePickers)
    }
  }, [datePickerOpen, categoryPickerOpen])

  const changeCategory = (category: TabItem) => {
    const veloCategoryId = getNullableCategoryId(category.id)
    const oldVeloCategoryId = getNullableCategoryId(selectedCategory.id)

    changeCalendarCategory({
      veloCategoryId,
      oldVeloCategoryId,
      referenceMonth: getReferenceMonthForSelectedDate(referenceDayDate, timezone),
      referenceDay,
    })

    setCategoryPickerOpen(false)
    focusElement({
      selector: hookToAttributeSelector(DH.calendarCategoryPickerButton, {componentId}),
      canFocus: true,
      preventScroll: true,
    })
  }

  const changeDate = (date: Date) => {
    if (date && date instanceof Date) {
      setDatePickerOpen(false)
      loadCalendar({
        referenceMonth: getReferenceMonthForSelectedDate(date, timezone),
        referenceDay: getReferenceDayForSelectedDate(date, timezone),
        origin: CalendarLoading.DEFAULT,
      })
      focusElement({
        selector: hookToAttributeSelector(DH.calendarDatePickerButton, {componentId}),
        canFocus: true,
        preventScroll: true,
      })
    }
  }

  const changeMonth = (date: Date) => {
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1)

    loadCalendar({
      referenceMonth: getReferenceMonthForSelectedDate(monthStart, timezone),
      referenceDay: getReferenceDayForSelectedDate(monthStart, timezone),
      origin: CalendarLoading.DEFAULT,
    })
  }

  const setCalendarToToday = () => {
    loadCalendar({
      referenceMonth: getReferenceMonthForSelectedDate(todayReferenceDate, timezone),
      referenceDay: todayReference,
      origin: CalendarLoading.DEFAULT,
    })
  }

  return (
    <div className={s.container}>
      {isListMainTitleEnabled() && <div className={s.titleText}>{title}</div>}
      <div className={s.pickers}>
        <div ref={datePickerRef}>
          <TextButton
            className={classNames(classes.datePickerButtonText, s.datePickerButtonText)}
            suffixIcon={<ChevronDown />}
            data-hook={DH.calendarDatePickerButton}
            hoverStyle={HoverStyles.underline}
            priority={TextButtonPriority.primary}
            onClick={() => setDatePickerOpen(!datePickerOpen)}
          >
            {getFullMonthText(referenceDayDate, locale)}
          </TextButton>
          {datePickerOpen && (
            <div className={s.datePickerOverlay}>
              {loading && (
                <div className={s.datePickerLoadingOverlay} data-hook={DH.calendarDatePickerLoader}>
                  <Spinner />
                </div>
              )}
              <DatePicker
                showMonthDropdown
                showYearDropdown
                className={classes.datePicker}
                today={todayReferenceDate}
                value={referenceDayDate}
                locale={locale}
                firstDayOfWeek={startWeekDay}
                leftArrowAriaLabel={t('a11y.previousMonth')}
                rightArrowAriaLabel={t('a11y.nextMonth')}
                onChange={changeDate}
                onMonthChange={changeMonth}
              />
            </div>
          )}
        </div>
        {categoryPickerVisible && (
          <div ref={categoryPickerRef}>
            <TextButton
              className={classNames(classes.categoryPickerButtonText, s.categoryPickerButtonText)}
              suffixIcon={<ChevronDown />}
              data-hook={DH.calendarCategoryPickerButton}
              hoverStyle={HoverStyles.underline}
              priority={TextButtonPriority.primary}
              onClick={() => setCategoryPickerOpen(!categoryPickerOpen)}
            >
              {selectedCategory ? selectedCategory.title : <Spinner calendarCategoryPickerButton diameter={20} />}
            </TextButton>
            {categoryPickerOpen && (
              <div className={s.categoryList}>
                <ActionsMenuLayout>
                  {categories.map(category => (
                    <ActionsMenuLayout.Item
                      key={category.id}
                      content={category.title}
                      onClick={() => changeCategory(category)}
                    />
                  ))}
                </ActionsMenuLayout>
              </div>
            )}
          </div>
        )}
      </div>
      <TodayButton onClick={setCalendarToToday} />
    </div>
  )
}
